import { structureHasGeneralPartner } from './copy';
import type { PartnershipStructure } from './types';

// Currency display for the ownership node: ISO-4217 *code* prefix, not symbol
// (e.g. "USD 100,000", "EUR 25,000"), per the locked spec. Right-alignment in
// tables is a presentational concern handled by the table cells.
export function formatMoneyWithCode(
  currencyCode: string,
  amount: number,
  options?: { maximumFractionDigits?: number }
): string {
  const formatted = new Intl.NumberFormat('en-US', {
    maximumFractionDigits: options?.maximumFractionDigits ?? 2,
  }).format(amount);
  return `${currencyCode} ${formatted}`;
}

// Form-summary rendering for the Ownership node's composite value. Shared by
// both FormSummary and the submission Review so they read identically, instead
// of dumping the raw { selectedIds, entries, <variant config> } object.

function ownerSummaryName(owner: unknown): string {
  const o = owner as {
    kind?: string;
    companyLegalName?: string;
    firstName?: string;
    lastName?: string;
  } | null;
  if (!o) return 'Owner';
  if (o.kind === 'entity') return o.companyLegalName?.trim() || 'Entity';
  return (
    [o.firstName, o.lastName].filter(Boolean).join(' ').trim() || 'Individual'
  );
}

// True for an Ownership value. Distinguished from KeyPersons (same
// { selectedIds, entries } shape) by a variant config key or an entry's typed
// `owner` — KeyPersons entries are flat field records.
export function isOwnershipValue(obj: Record<string, unknown>): boolean {
  if (!('selectedIds' in obj) || !('entries' in obj)) return false;
  if ('shares' in obj || 'membership' in obj || 'partnership' in obj)
    return true;
  const { entries } = obj;
  if (typeof entries !== 'object' || entries === null) return false;
  return Object.values(entries as Record<string, unknown>).some((e) => {
    const owner = (e as { owner?: { kind?: string } } | null)?.owner;
    return (
      owner != null && (owner.kind === 'individual' || owner.kind === 'entity')
    );
  });
}

export function formatOwnershipValue(
  value: unknown,
  // The node's configured structure, for values saved before it was persisted
  // into the value itself.
  fallbackStructure?: PartnershipStructure
): string {
  const v = value as {
    selectedIds?: string[];
    entries?: Record<
      string,
      {
        owner?: unknown;
        ownershipPercent?: number;
        isGeneralPartner?: boolean;
        governanceRole?: string;
      }
    >;
  } | null;
  const ids = v?.selectedIds ?? [];
  const entries = v?.entries ?? {};
  const cfg = (value ?? {}) as {
    membership?: unknown;
    partnership?: unknown;
    partnershipStructure?: PartnershipStructure;
  };
  const holdingType = cfg.membership ? 'Membership (LLC)' : cfg.partnership ? 'Partnership' : 'Shares (corporation)';
  const gpApplies = structureHasGeneralPartner(
    cfg.partnershipStructure ?? fallbackStructure
  );
  const lines = ids
    .map((id) => entries[id])
    .filter((e): e is NonNullable<typeof e> => e != null)
    .map((e) => {
      const name = ownerSummaryName(e.owner);
      const pct = e.ownershipPercent != null ? `${e.ownershipPercent}%` : '—';
      const role =
        gpApplies && e.isGeneralPartner
          ? ' (General Partner)'
          : e.governanceRole === 'MANAGER'
            ? ' (Manager)'
            : '';
      return `${name} — ${pct}${role}`;
    });
  return lines.length > 0 ? `${holdingType}: ${lines.join(', ')}` : '—';
}
